import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarIcon, Clock, MapPin, Users, Trophy, XCircle } from 'lucide-react';
import { GameWithDetails } from '@/types/gameSystem';
import { useGameCountdown } from '@/hooks/useGameManagement';
import { useGameRealTimeUpdates } from '@/hooks/useGameRealTimeUpdates';
import { format } from 'date-fns';
import { TeamGameCard } from './TeamGameCard';
import { ForfeitBadge } from './ForfeitBadge';
import { EnhancedCountdownTimer } from './EnhancedCountdownTimer';
import { TeamSearchCombobox } from '../venue/TeamSearchCombobox';
import { TeamAssignmentForm } from './TeamAssignmentForm';
import { PlayerJoinRequestButton } from './PlayerJoinRequestButton';
import { toast } from 'sonner';
import { venueGamesService } from '@/services/venueGamesService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useTeamAuthCheck } from '@/hooks/useTeamAuthCheck';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';


interface GameCardProps {
  game: GameWithDetails;
  onConfirm?: (teamSide: 'home' | 'away') => void;
  onManageRoster?: () => void;
  onViewDetails?: () => void;
  onAssignTeam?: (teamSide: 'home' | 'away', teamId: number, teamName: string) => void;
  showActions?: boolean;
  currentUserId?: string;
}

export const GameCard = ({ 
  game, 
  onConfirm, 
  onManageRoster, 
  onViewDetails,
  onAssignTeam,
  showActions = true,
  currentUserId 
}: GameCardProps) => {
  const countdown = useGameCountdown(game.countdown_end_at);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { permissions } = useUserRole();
  
  // Add real-time updates
  useGameRealTimeUpdates({
    gameId: game.id?.toString(),
    userId: currentUserId
  });

  const handleApplyForScorekeeper = async () => {
    const currentUser = supabaseUser || internalUser;
    if (!currentUser?.id) {
      toast.error('Please log in to apply for scorekeeper position');
      return;
    }

    // Check if user has scorekeeper role
    if (supabaseUser) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();
      
      if (profile?.role !== 'scorekeeper') {
        toast.error('Only users with ScoreKeeper accounts can apply for scorekeeper positions');
        return;
      }
    } else if (internalUser) {
      const { data: user } = await supabase
        .from('internal_users')
        .select('role')
        .eq('id', currentUser.id)
        .single();
      
      if (user?.role !== 'scorekeeper') {
        toast.error('Only users with ScoreKeeper accounts can apply for scorekeeper positions');
        return;
      }
    }

    // Prevent venue owners from applying as scorekeepers for their own games
    if (game.venue_id === currentUser.id) {
      toast.error('Venue owners cannot apply as scorekeepers for their own games');
      return;
    }

    try {
      await venueGamesService.assignScorekeeper(game.id, currentUser.id);
      toast.success('Successfully applied for scorekeeper position!');
    } catch (error) {
      console.error('Error applying for scorekeeper:', error);
      toast.error('Failed to apply for scorekeeper position');
    }
  };

  const getStatusBadge = () => {
    switch (game.game_status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'confirmed':
        return <Badge variant="default">Confirmed</Badge>;
      case 'in_progress':
        return <Badge variant="destructive">In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      case 'forfeited':
        return <Badge variant="destructive">Forfeited</Badge>;
      default:
        return <Badge variant="secondary">{game.game_status}</Badge>;
    }
  };

  const formatCountdown = () => {
    if (!countdown) return null;
    
    if (countdown.expired) {
      return <span className="text-destructive font-medium">Time Expired</span>;
    }

    if (countdown.days > 0) {
      return `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`;
    } else if (countdown.hours > 0) {
      return `${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`;
    } else {
      return `${countdown.minutes}m ${countdown.seconds}s`;
    }
  };

  const isTeamConfirmed = (teamSide: 'home' | 'away') => {
    // For venue games, check the team ready status
    if (game.source === 'venue') {
      const isTeamReady = teamSide === 'home' ? game.home_team_ready : game.away_team_ready;
      return Boolean(isTeamReady);
    }
    
    // For regular games, check confirmations table
    const hasConfirmation = game.confirmations?.some(c => c.team_side === teamSide && c.confirmation_status === true);
    return Boolean(hasConfirmation);
  };

  const canConfirmTeam = (teamSide: 'home' | 'away') => {
    const teamId = teamSide === 'home' ? game.home_team_id : game.away_team_id;
    if (!teamId || !currentUserId || isTeamConfirmed(teamSide)) return false;
    
    // Check if the user is a coach or owner of this specific team
    const userRole = permissions.role;
    if (!['coach', 'team-owner', 'admin'].includes(userRole)) return false;
    
    // For venue games, only allow coaches/owners of the assigned team to confirm
    return canUserManageTeam(teamId);
  };

  const { canManageTeam: canManageHomeTeam } = useTeamAuthCheck({ teamId: game.home_team_id || undefined });
  const { canManageTeam: canManageAwayTeam } = useTeamAuthCheck({ teamId: game.away_team_id || undefined });

  const canUserManageTeam = (teamId: number): boolean => {
    if (!currentUserId) return false;
    
    if (teamId === game.home_team_id) {
      return canManageHomeTeam;
    } else if (teamId === game.away_team_id) {
      return canManageAwayTeam;
    }
    
    return false;
  };

  const canAddTeam = (teamSide: 'home' | 'away') => {
    const teamId = teamSide === 'home' ? game.home_team_id : game.away_team_id;
    return !teamId && currentUserId && ['pending', 'approved'].includes(game.game_status);
  };

  const canManageRoster = () => {
    if (!currentUserId) return false;
    
    // Cannot manage roster for expired, cancelled, forfeited or completed games
    if (['cancelled', 'forfeited', 'completed', 'in_progress'].includes(game.game_status)) {
      return false;
    }
    
    // Check if game time has passed
    const gameDateTime = new Date(`${game.game_date} ${game.game_time}`);
    const now = new Date();
    if (now > gameDateTime) {
      return false;
    }
    
    // Game creator can manage roster for active games
    if (game.created_by === currentUserId) return true;
    
    // Venue owner can manage roster for active games
    if (game.venue_id === currentUserId) return true;
    
    // Team coaches and owners can manage roster for their teams (for confirmed/pending games)
    return ['confirmed', 'pending', 'approved'].includes(game.game_status);
  };

  const getParticipantCount = (teamSide: 'home' | 'away') => {
    // Count participants who are confirmed, ready to play, or requested (pending approval)
    const participantCount = game.participants?.filter(p => 
      p.team_side === teamSide && 
      ['confirmed', 'ready_to_play', 'requested'].includes(p.participation_status)
    ).length || 0;
    
    // Count roster members (from game_rosters table)
    const rosterCount = (game as any).rosters?.filter((r: any) => 
      r.team_side === teamSide && r.is_eligible
    ).length || 0;

    // Debug logging to help troubleshoot
    console.log(`🏀 Participant count for ${teamSide} team in game ${game.game_code}:`, {
      gameId: game.id,
      teamSide,
      participants: game.participants?.length || 0,
      rosters: (game as any).rosters?.length || 0,
      participantCount,
      rosterCount,
      totalCount: participantCount + rosterCount
    });
    
    // Return the total count
    return participantCount + rosterCount;
  };

  const formatGameTime = (time: string, timezone?: string) => {
    try {
      // Parse the time string (assuming it's in HH:mm format)
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      // Format to 12-hour time with AM/PM in CST
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      // Always show CST timezone
      return `${formattedTime} CST`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time; // fallback to original time
    }
  };

  // Check if user has already requested to join
  const currentUser = supabaseUser || internalUser;
  const isAlreadyRequested = game.participants?.some(p => 
    p.player_id === currentUser?.id && p.participation_status === 'requested'
  ) || false;


  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                {game.game_name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Game Code: <span className="font-mono font-medium">{game.game_code}</span>
              </p>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Date and Time */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {format(new Date(game.game_date), 'MMM dd, yyyy')}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {formatGameTime(game.game_time, game.game_timezone)}
            </div>
            {(game as any).sport_name && (
              <div className="inline-flex items-center px-2 py-1 rounded-full bg-primary/10 text-primary font-medium text-xs">
                {(game as any).sport_name}
              </div>
            )}
          </div>

          {/* Venue */}
          {game.venue_name && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <div>
                  <span>{game.venue_name}</span>
                  {game.venue_address && (
                    <div className="text-muted-foreground">{game.venue_address}</div>
                  )}
                  {(game as any).city && (game as any).state_province && (
                    <div className="text-muted-foreground text-xs">
                      {(game as any).city}, {(game as any).state_province}
                    </div>
                  )}
                </div>
              </div>
              {game.venue_name !== 'TBD' && (
               <Button
                  variant="outline"
                  size="sm"
                  className="text-xs px-2 py-1"
                  onClick={() => toast.success('Venue followed successfully!')}
                >
                  Follow Venue
                </Button>
              )}
            </div>
          )}

          {/* Teams */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TeamGameCard
              teamName={game.home_team?.team_name || (game.home_team_id ? 'Loading...' : 'Open')}
              teamLogo={game.home_team?.team_logo}
              teamLabel={game.home_team_label || 'Home'}
              isConfirmed={isTeamConfirmed('home')}
              isForfeited={game.game_status === 'forfeited' && game.forfeit_reason?.includes('home')}
              forfeitReason={game.forfeit_reason}
              participantCount={getParticipantCount('home')}
              minimumPlayers={game.minimum_players_required}
              side="home"
            />

            <TeamGameCard
              teamName={game.away_team?.team_name || (game.away_team_id ? 'Loading...' : 'Open')}
              teamLogo={game.away_team?.team_logo}
              teamLabel={game.away_team_label || 'Away'}
              isConfirmed={isTeamConfirmed('away')}
              isForfeited={game.game_status === 'forfeited' && game.forfeit_reason?.includes('away')}
              forfeitReason={game.forfeit_reason}
              participantCount={getParticipantCount('away')}
              minimumPlayers={game.minimum_players_required}
              side="away"
            />
          </div>

          {/* Enhanced Countdown Timer */}
          {game.game_status === 'confirmed' && game.countdown_end_at && countdown && (
            <EnhancedCountdownTimer
              countdown={countdown}
              variant="detailed"
            />
          )}

          {/* Enhanced Forfeit Information */}
          {game.game_status === 'forfeited' && (
            <div className="bg-destructive/5 p-4 rounded-lg border border-destructive/20">
              <ForfeitBadge reason={game.forfeit_reason} variant="detailed" />
            </div>
          )}

          {/* Cancellation Information */}
          {game.game_status === 'cancelled' && (
            <div className="bg-destructive/5 p-4 rounded-lg border border-destructive/20">
              <div className="flex items-center gap-2 text-destructive">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">Game Cancelled</span>
              </div>
              <p className="text-sm text-destructive/80 mt-1">
                {game.cancellation_reason || 'Game has been cancelled'}
              </p>
            </div>
          )}

          {/* Scorekeeper Section */}
          {(game as any).scorekeeper_needed && !(game as any).scorekeeper_assigned && (
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary font-medium text-sm">Scorekeeper Needed</p>
                  <p className="text-muted-foreground text-xs">
                    Earn {(game as any).scorekeeper_points || 50} player points
                  </p>
                  <p className="text-muted-foreground text-xs">
                    *Points awarded only upon game completion
                  </p>
                </div>
                <Button 
                  onClick={handleApplyForScorekeeper}
                  size="sm"
                  className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          {/* Scorekeeper Assigned */}
          {(game as any).scorekeeper_assigned && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <p className="text-green-700 dark:text-green-400 font-medium text-sm">
                ✓ Scorekeeper Assigned
              </p>
            </div>
          )}

          {/* Team Assignment Form */}
          {(canAddTeam('home') || canAddTeam('away')) && (
            <TeamAssignmentForm
              game={game}
              onAssignTeam={(teamSide, teamId, teamName) => {
                if (onAssignTeam) {
                  onAssignTeam(teamSide, teamId as number, teamName);
                }
              }}
              currentUserId={currentUserId}
            />
          )}

           {/* Action Buttons */}
          {showActions && (
            <div className="flex flex-wrap gap-2 pt-2">
              {/* For scorekeeper role users - only show scorekeeper-specific buttons */}
              {permissions?.role === 'scorekeeper' ? (
                <>
                  {/* View Details button for scorekeepers */}
                  {onViewDetails && (
                    <Button variant="outline" size="sm" onClick={onViewDetails} className="flex items-center gap-2">
                      <div className="flex -space-x-1">
                        {game.home_team?.team_logo && (
                          <img src={game.home_team.team_logo} alt="Home team" className="w-4 h-4 rounded-full border border-white" />
                        )}
                        {game.away_team?.team_logo && (
                          <img src={game.away_team.team_logo} alt="Away team" className="w-4 h-4 rounded-full border border-white" />
                        )}
                      </div>
                      View Details
                    </Button>
                  )}

                  {/* Scorekeeper application for scorekeeper role */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleApplyForScorekeeper}
                    className="text-xs px-3 py-1"
                  >
                    Apply as Scorekeeper
                  </Button>
                </>
              ) : (
                <>
                  {/* For all other roles - existing logic */}
                  {onViewDetails && (
                    <Button variant="outline" size="sm" onClick={onViewDetails} className="flex items-center gap-2">
                      <div className="flex -space-x-1">
                        {game.home_team?.team_logo && (
                          <img src={game.home_team.team_logo} alt="Home team" className="w-4 h-4 rounded-full border border-white" />
                        )}
                        {game.away_team?.team_logo && (
                          <img src={game.away_team.team_logo} alt="Away team" className="w-4 h-4 rounded-full border border-white" />
                        )}
                      </div>
                      View Details
                    </Button>
                  )}

                  {/* Home Team Confirmation */}
                  {canConfirmTeam('home') && !isTeamConfirmed('home') && (
                    <Button 
                      size="sm" 
                      onClick={() => onConfirm?.('home')}
                      className="text-xs bg-green-600 hover:bg-green-700"
                    >
                      Confirm Home Team
                    </Button>
                  )}

                  {isTeamConfirmed('home') && (
                    <div className="px-3 py-2 bg-green-100 dark:bg-green-900 rounded-lg text-green-700 dark:text-green-300 text-xs font-medium">
                      ✓ Home Team Confirmed
                    </div>
                  )}

                   {/* Away Team Confirmation */}
                   {canConfirmTeam('away') && !isTeamConfirmed('away') && (
                     <Button 
                       size="sm" 
                       onClick={() => onConfirm?.('away')}
                       className="text-xs bg-blue-600 hover:bg-blue-700"
                     >
                       Confirm Away Team
                     </Button>
                   )}

                   {isTeamConfirmed('away') && (
                     <div className="px-3 py-2 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-700 dark:text-blue-300 text-xs font-medium">
                       ✓ Away Team Confirmed
                     </div>
                   )}

                   {/* Game confirmation after both teams are confirmed */}
                   {isTeamConfirmed('home') && isTeamConfirmed('away') && ['confirmed', 'pending'].includes(game.game_status) && (
                     <Button 
                       size="sm" 
                       onClick={() => toast.success('Game confirmed successfully!')}
                       className="text-xs bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                     >
                       Confirm Game
                     </Button>
                   )}

                  {onManageRoster && canManageRoster() && (
                    <Button variant="secondary" size="sm" onClick={onManageRoster}>
                      Manage Roster
                    </Button>
                  )}

                    {/* Request to Join Button - Show for pending and confirmed games that user hasn't joined yet and isn't a team manager */}
                    {['pending', 'confirmed'].includes(game.game_status) && currentUserId && 
                     !canUserManageTeam(game.home_team_id || 0) && 
                     !canUserManageTeam(game.away_team_id || 0) && (
                       <PlayerJoinRequestButton
                         gameId={game.id}
                         isAlreadyRequested={isAlreadyRequested}
                         isGameLocked={false}
                         gameStatus={game.game_status}
                         homeTeamName={game.home_team_label || 'Home Team'}
                         awayTeamName={game.away_team_label || 'Away Team'}
                         className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                       />
                    )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
